import { useEffect, useState } from "react"
import { Input } from "antd"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { SearchGlassIcon } from "components/ui/icons/input/SearchGlassIcon"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ModelNumberRow } from "components/ui/customWebviewer/FurnitureSidebar/layout/ModelNumberSection/SelectModelNumberModal/ModelNumberRow"
import { useSearchModelNumbers } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useDebounce } from "hooks/debounce.hook"
import { MIN_SEARCH_CHARS, flattenCatalogue } from "helpers/modelNumberCatalogue.helper"
import type { TCatalogueModelNumber } from "types/modelNumbers.type"
import style from "./SelectBaseModelNumberModal.module.scss"

const { modal, header, title, closeButton, searchWrapper, searchIcon, list, row, stateBlock, stateTitle, stateSubtitle, footer, backButton, footerButtons } = style

const SEARCH_DEBOUNCE_MS = 300

export type TBaseModelNumber = {
	id: number
	model_number: string
}

type Props = {
	isOpen: boolean
	initialSelected: TBaseModelNumber | null
	onClose: () => void
	onBack: () => void
	onSave: (model: TBaseModelNumber) => void
	/**
	 * Where the modal portals to. Omitted here on the management screen (antd's default,
	 * `document.body`, is correct there); the quotation editor passes its own shadow-DOM container,
	 * since that page lives inside the Apryse shadow root.
	 */
	getContainer?: () => HTMLElement
}

export const SelectBaseModelNumberModal = ({
	isOpen,
	initialSelected,
	onClose,
	onBack,
	onSave,
	getContainer,
}: Props) => {
	const [searchTerm, setSearchTerm] = useState("")
	// Holds the full picked record, not just an id looked up from `results` — a search
	// that no longer matches the pick must not silently disable Save (AGENTS.md's
	// "the remembered selection is state, not a row" rule for SelectModelNumberModal).
	const [pinnedModel, setPinnedModel] = useState<TBaseModelNumber | null>(initialSelected)
	const debouncedTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS)
	const trimmedTerm = debouncedTerm.trim()
	const hasEnoughChars = trimmedTerm.length >= MIN_SEARCH_CHARS

	const searchQuery = useSearchModelNumbers(trimmedTerm, hasEnoughChars)
	const results = flattenCatalogue(searchQuery.data)

	useEffect(() => {
		if (!isOpen) return
		setSearchTerm("")
		setPinnedModel(initialSelected)
	}, [isOpen, initialSelected])

	const save = () => {
		if (pinnedModel === null) return
		onSave(pinnedModel)
	}

	const renderState = () => {
		if (!hasEnoughChars) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Keep typing to search model numbers</span>
					<span className={stateSubtitle}>Enter at least {MIN_SEARCH_CHARS} characters.</span>
				</div>
			)
		}
		if (searchQuery.isFetching || debouncedTerm.trim() !== searchTerm.trim()) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Searching…</span>
				</div>
			)
		}
		if (searchQuery.isError) {
			return (
				<div className={stateBlock}>
					<span className={stateTitle}>Couldn&apos;t search model numbers</span>
					<span className={stateSubtitle}>The search request failed. Check your connection and try again.</span>
				</div>
			)
		}
		return (
			<div className={stateBlock}>
				<span className={stateTitle}>No model numbers match “{trimmedTerm}”</span>
			</div>
		)
	}

	const selectRow = (model: TCatalogueModelNumber) => setPinnedModel({ id: model.id, model_number: model.model_number })

	return (
		<Modal
			className={modal}
			isModalOpen={isOpen}
			setModalClose={onClose}
			width={600}
			closable={false}
			getContainer={getContainer}
			styles={{ content: { padding: 0 }, body: { padding: 0 } }}
		>
			<div className={header}>
				<h2 className={title}>Select base model number</h2>
				<button className={closeButton} type="button" onClick={onClose} aria-label="Close modal">
					<CloseIcon />
				</button>
			</div>
			<div className={searchWrapper}>
				<Input
					placeholder="Search by name, description or price"
					prefix={<SearchGlassIcon className={searchIcon} />}
					size="large"
					value={searchTerm}
					onChange={(event) => setSearchTerm(event.target.value)}
				/>
			</div>
			<div className={list}>
				{results.length === 0
					? renderState()
					: results.map((model) => (
							<ModelNumberRow
								key={model.id}
								className={row}
								model={model}
								isSelected={model.id === pinnedModel?.id}
								isMlChoice={false}
								thumbnailUrl={model.thumbnails_url}
								onSelect={() => selectRow(model)}
							/>
						))}
			</div>
			<div className={footer}>
				<button className={backButton} type="button" onClick={onBack}>
					Back
				</button>
				<div className={footerButtons}>
					<ButtonSecondary type="button" onClick={onClose}>
						Cancel
					</ButtonSecondary>
					<ButtonPrimary submittable={pinnedModel !== null} onClick={save}>
						Use as base
					</ButtonPrimary>
				</div>
			</div>
		</Modal>
	)
}
